import {
  type AppError,
  InvalidTokenError,
  type ResultWithOauthToken,
} from '@packages/utils';
import {
  type UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import type {ZodSchema, z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import type {
  OverallStatusType,
  SortKeyType,
  SortOrderType,
} from '../../features/entryManagements/constants';
import type {
  EntryFormOriginType,
  StepCreationTriggerType,
} from '../../features/entryManagements/constants/entrySourceTypes';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {
  GetMissionsOutput,
  type MissionType,
} from '../entities/entryManagements/missionsSchema';
import {
  AnalyzeStepCandidatesResponse,
  type AnalyzeStepOutput,
  CreateEntryManagementOutput,
  CreateStepOutput,
  DeleteEntryManagementOutput,
  DeleteStepOutput,
  EntryManagementDetail,
  type EntryManagementSummary,
  GetEntryManagementsOutput,
  type LatestStep,
  RemindersResponse,
  type StepCandidate,
  UpdateEntryManagementOutput,
} from '../entities/entryManagements/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';

export type EntryManagementSummaryType = z.infer<typeof EntryManagementSummary>;
export type LatestStepType = z.infer<typeof LatestStep>;
export type StepCandidateType = z.infer<typeof StepCandidate>;

// GET /3.0/users/me/entry-managements
type GetEntryManagementsArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  /** ページ番号（1以上の整数） */
  page?: number;
  /** 1ページあたりの件数（1-100の範囲） */
  limit?: number;
  /** エントリー開始日の絞り込み（YYYY-MM-DD形式） */
  startDate?: string;
  /** エントリー終了日の絞り込み（YYYY-MM-DD形式） */
  endDate?: string;
  /** 企業名での絞り込み */
  companyName?: string;
  /** ソートキー: created | modified | latestStepDeadline | latestStepStartDate */
  sortBy?: SortKeyType;
  /** ソート順序: asc | desc */
  sortOrder?: SortOrderType;
  /** 全体ステータスでの絞り込み: active | completed | archived | rejected */
  overallStatus?: OverallStatusType | OverallStatusType[];
  /** エントリー完了状態での絞り込み */
  hasCompletedEntry?: boolean;
  /** 選考ステップ有無での絞り込み */
  hasSteps?: boolean;
  /** 未完了ステップ（最新ステップ）有無での絞り込み */
  hasLatestStep?: boolean;
  /** 取得する最新ステップの数（1-2、デフォルト2） */
  latestStepCount?: number;
};

export type GetEntryManagementsResponse = z.infer<
  typeof GetEntryManagementsOutput
>;

export type GetEntryManagementsReturnValueType = ResultWithOauthToken<
  GetEntryManagementsResponse,
  AppError
>;

type UseGetEntryManagementsOptions = {
  config?: UseQueryOptions<GetEntryManagementsReturnValueType>;
} & Omit<GetEntryManagementsArgs, 'cookies'>;

type QueryParamValue = string | string[] | number | boolean;

/**
 * リクエストパラメータを構築するヘルパー関数
 * undefined値を持つプロパティを除外してクリーンなオブジェクトを作成
 */
const buildRequestParams = (
  params: Record<string, QueryParamValue | undefined>,
): Record<string, QueryParamValue> => {
  return Object.entries(params).reduce<Record<string, QueryParamValue>>(
    (result, [key, value]) => {
      if (value !== undefined) {
        result[key] = value;
      }
      return result;
    },
    {},
  );
};

export const getEntryManagements = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  page,
  limit,
  startDate,
  endDate,
  companyName,
  sortBy,
  sortOrder,
  overallStatus,
  hasCompletedEntry,
  hasSteps,
  hasLatestStep,
  latestStepCount,
}: GetEntryManagementsArgs): Promise<GetEntryManagementsReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    // クエリパラメータを構築
    const queryParams = buildRequestParams({
      page,
      limit,
      startDate,
      endDate,
      companyName,
      sortBy,
      sortOrder,
      overallStatus,
      hasCompletedEntry,
      hasSteps,
      hasLatestStep,
      latestStepCount,
    });

    const path = createPathWithQuery(
      `/${version}/users/me/entry-managements`,
      queryParams,
      'none', // 配列の場合: key=value1&key=value2 形式
    );

    return defaultClient.get(path, GetEntryManagementsOutput);
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return result;
};

export const useGetEntryManagements = ({
  config,
  page,
  limit,
  startDate,
  endDate,
  companyName,
  sortBy,
  sortOrder,
  overallStatus,
  hasCompletedEntry,
  hasSteps,
  hasLatestStep,
  latestStepCount,
}: UseGetEntryManagementsOptions = {}) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<GetEntryManagementsReturnValueType>({
    ...config,
    queryKey: [
      'entryManagements',
      page,
      limit,
      startDate,
      endDate,
      companyName,
      sortBy,
      sortOrder,
      overallStatus,
      hasCompletedEntry,
      hasSteps,
      hasLatestStep,
      latestStepCount,
    ],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getEntryManagements({
        cookies,
        page,
        limit,
        startDate,
        endDate,
        companyName,
        sortBy,
        sortOrder,
        overallStatus,
        hasCompletedEntry,
        hasSteps,
        hasLatestStep,
        latestStepCount,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

// GET /3.0/users/me/entry-managements/{id}
type GetEntryManagementArgs = {
  cookies: Partial<Record<string, string>>;
  id: number;
  version?: string;
};

export type GetEntryManagementResponse = z.infer<typeof EntryManagementDetail>;

export type GetEntryManagementReturnValueType = ResultWithOauthToken<
  GetEntryManagementResponse,
  AppError
>;

type UseGetEntryManagementOptions = {
  config?: UseQueryOptions<GetEntryManagementReturnValueType>;
};

export const getEntryManagement = async ({
  cookies,
  id,
  version = DEFAULT_API_VERSION,
}: GetEntryManagementArgs): Promise<GetEntryManagementReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-managements/${id}`;

    return defaultClient.get(path, EntryManagementDetail);
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return result;
};

export const useGetEntryManagement = (
  id: number,
  {config}: UseGetEntryManagementOptions = {},
) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<GetEntryManagementReturnValueType>({
    ...config,
    queryKey: ['entryManagement', id],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getEntryManagement({cookies, id});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

// POST /3.0/users/me/entry-managements
/** ステップの共通フィールド */
type BaseStepFields = {
  stepName: string;
  sourceType?: EntryFormOriginType;
  entryTitle?: string;
  entrySourceUrl?: string;
  entrySourceType: StepCreationTriggerType;
  entryDate?: string;
  notes?: string;
  stepDate?: string;
  deadline?: string;
  startDate?: string;
  endDate?: string;
  place?: string;
};

type CreateEntryManagementStepArgs = BaseStepFields & {
  stepOrder: number;
  scheduleID?: number;
  result?: string;
};

type CreateEntryManagementArgs = {
  cookies: Partial<Record<string, string>>;
  companyID: number; // gsCompanyIdからcompanyIDに変更
  companyName: string;
  steps?: CreateEntryManagementStepArgs[]; // 初期ステップ配列
  version?: string;
};

export type CreateEntryManagementResponse = z.infer<
  typeof CreateEntryManagementOutput
>;

export type CreateEntryManagementReturnValueType = ResultWithOauthToken<
  CreateEntryManagementResponse,
  AppError
>;

export const createEntryManagement = async ({
  cookies,
  companyID,
  companyName,
  steps,
  version = DEFAULT_API_VERSION,
}: CreateEntryManagementArgs): Promise<CreateEntryManagementReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-managements`;

    const body = {
      companyID,
      companyName,
      ...(steps && {steps}),
    };

    return defaultClient.post(path, CreateEntryManagementOutput, body);
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return result;
};

export const useCreateEntryManagement = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    CreateEntryManagementReturnValueType,
    Error,
    Omit<CreateEntryManagementArgs, 'cookies'>
  >({
    async mutationFn(args) {
      const cookies = parseCookies();
      const result = await createEntryManagement({...args, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }
      return result;
    },
  });
};

// PUT /3.0/service/users/me/entry-managements/{id} - エントリー管理更新API

/** オプショナルフィールドにnullを許容する型（tri-state対応: undefined=保持, null=削除, 値=更新） */
type WithNullableOptionalFields<T> = {
  [K in keyof T]: undefined extends T[K] ? T[K] | null : T[K];
};

type UpdateEntryManagementStepArgs =
  WithNullableOptionalFields<BaseStepFields> & {
    id?: number; // 既存ステップの場合は必須、新規は不要
    stepOrder: number;
    result?: string | null;
    entrySourceType?: BaseStepFields['entrySourceType'] | null;
  };

type UpdateEntryManagementArgs = {
  cookies: Partial<Record<string, string>>;
  id: number;
  companyID: number;
  companyName: string;
  overallStatus?: string;
  hasCompletedEntry?: boolean;
  steps?: UpdateEntryManagementStepArgs[];
  version?: string;
};

export type UpdateEntryManagementResponse = z.infer<
  typeof UpdateEntryManagementOutput
>;

export type UpdateEntryManagementReturnValueType = ResultWithOauthToken<
  UpdateEntryManagementResponse,
  AppError
>;

export const updateEntryManagement = async ({
  cookies,
  id,
  companyID,
  companyName,
  overallStatus,
  hasCompletedEntry,
  steps,
  version = DEFAULT_API_VERSION,
}: UpdateEntryManagementArgs): Promise<UpdateEntryManagementReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-managements/${id}`;

    const body = {
      companyID,
      companyName,
      ...(overallStatus !== undefined && {overallStatus}),
      ...(hasCompletedEntry !== undefined && {hasCompletedEntry}),
      ...(steps && {steps}),
    };

    return defaultClient.put(path, UpdateEntryManagementOutput, body);
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return result;
};

export const useUpdateEntryManagement = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    UpdateEntryManagementReturnValueType,
    Error,
    Omit<UpdateEntryManagementArgs, 'cookies'>
  >({
    async mutationFn(args) {
      const cookies = parseCookies();
      const result = await updateEntryManagement({...args, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }
      return result;
    },
  });
};

// POST /3.0/service/users/me/entry-managements/steps - ステップ自動生成API
type CreateStepArgs = BaseStepFields & {
  cookies: Partial<Record<string, string>>;
  companyID?: number;
  companyName: string;
  stepOrder?: number;
  recruitmentItemScheduleID?: number;
  recruitmentStepScheduleID?: number;
  resultStatus?: string;
  hasCompletedEntry?: boolean;
  version?: string;
};

export type CreateStepResponse = z.infer<typeof CreateStepOutput>;

export type CreateStepReturnValueType = ResultWithOauthToken<
  CreateStepResponse,
  AppError
>;

export const createStep = async ({
  cookies,
  companyID,
  companyName,
  stepName,
  stepOrder,
  sourceType,
  entryTitle,
  entrySourceUrl,
  entrySourceType,
  entryDate,
  recruitmentItemScheduleID,
  recruitmentStepScheduleID,
  resultStatus,
  notes,
  stepDate,
  deadline,
  startDate,
  endDate,
  place,
  hasCompletedEntry,
  version = DEFAULT_API_VERSION,
}: CreateStepArgs): Promise<CreateStepReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-managements/steps`;

    const body = buildRequestParams({
      companyID,
      companyName,
      stepName,
      stepOrder,
      sourceType,
      entryTitle,
      entrySourceUrl,
      entrySourceType,
      entryDate,
      recruitmentItemScheduleID,
      recruitmentStepScheduleID,
      result: resultStatus,
      notes,
      stepDate,
      deadline,
      startDate,
      endDate,
      place,
      hasCompletedEntry,
    });

    return defaultClient.post(path, CreateStepOutput, body);
  };

  const apiResult = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return apiResult;
};

export const useCreateStep = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    CreateStepReturnValueType,
    Error,
    Omit<CreateStepArgs, 'cookies'>
  >({
    async mutationFn(args) {
      const cookies = parseCookies();
      const result = await createStep({...args, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }
      return result;
    },
  });
};

// DELETE /3.0/service/users/me/entry-managements/{id} - エントリー管理削除API
type DeleteEntryManagementArgs = {
  cookies: Partial<Record<string, string>>;
  id: number;
  version?: string;
};

export type DeleteEntryManagementResponse = z.infer<
  typeof DeleteEntryManagementOutput
>;

export type DeleteEntryManagementReturnValueType = ResultWithOauthToken<
  DeleteEntryManagementResponse,
  AppError
>;

export const deleteEntryManagement = async ({
  cookies,
  id,
  version = DEFAULT_API_VERSION,
}: DeleteEntryManagementArgs): Promise<DeleteEntryManagementReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-managements/${id}`;

    return defaultClient.delete(path, DeleteEntryManagementOutput);
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  // 削除APIは204 No Contentを返す可能性があるため、ApiResponseShapeErrorを成功として扱う
  if (!result.ok && result.error === 'ApiResponseShapeError') {
    return {
      ok: true as const,
      value: {googleSyncResult: undefined},
      accessToken: result.accessToken,
    };
  }

  return result;
};

export const useDeleteEntryManagement = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    DeleteEntryManagementReturnValueType,
    Error,
    Omit<DeleteEntryManagementArgs, 'cookies'>
  >({
    async mutationFn(args) {
      const cookies = parseCookies();
      const result = await deleteEntryManagement({...args, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }
      return result;
    },
  });
};

// DELETE /3.0/service/users/me/entry-managements/steps/{stepId} - ステップ削除API
type DeleteStepArgs = {
  cookies: Partial<Record<string, string>>;
  stepId: number;
  version?: string;
};

export type DeleteStepResponse = z.infer<typeof DeleteStepOutput>;

export type DeleteStepReturnValueType = ResultWithOauthToken<
  DeleteStepResponse,
  AppError
>;

export const deleteStep = async ({
  cookies,
  stepId,
  version = DEFAULT_API_VERSION,
}: DeleteStepArgs): Promise<DeleteStepReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-managements/steps/${stepId}`;

    return defaultClient.delete(path, DeleteStepOutput);
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  // 削除APIはGoogle未連携時に204 No Contentを返すため、ApiResponseShapeErrorを成功として扱う
  if (!result.ok && result.error === 'ApiResponseShapeError') {
    return {
      ok: true as const,
      value: {googleSyncResult: undefined},
      accessToken: result.accessToken,
    };
  }

  return result;
};

export const useDeleteStep = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    DeleteStepReturnValueType,
    Error,
    Omit<DeleteStepArgs, 'cookies'>
  >({
    async mutationFn(args) {
      const cookies = parseCookies();
      const result = await deleteStep({...args, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }
      return result;
    },
  });
};

// 共通型定義
export type AnalyzeStepResponse = z.infer<typeof AnalyzeStepOutput>;
export type AnalyzeStepReturnValueType = ResultWithOauthToken<
  AnalyzeStepResponse,
  AppError
>;

// 共通の解析ステップAPI実装関数
const analyzeStepRequest = async <
  TBody extends Record<string, unknown>,
  TSchema extends ZodSchema,
>({
  cookies,
  path,
  body,
  schema,
}: {
  cookies: Partial<Record<string, string>>;
  path: string;
  body: TBody;
  schema: TSchema;
}): Promise<ResultWithOauthToken<z.infer<TSchema>, AppError>> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    return defaultClient.post(path, schema, body);
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return result;
};

// POST /3.0/service/users/me/entry-managements/steps/text-analyze - エントリー管理ステップAI解析作成API
type TextAnalyzeStepArgs = {
  cookies: Partial<Record<string, string>>;
  text: string;
  companyID?: number;
  /** 既存のエントリー管理に紐づけるためのID */
  userEntryManagementID?: number;
  version?: string;
};

type TextAnalyzeStepRequestBody = {
  text: string;
  companyID?: number;
  userEntryManagementID?: number;
};

export type TextAnalyzeStepResponse = z.infer<
  typeof AnalyzeStepCandidatesResponse
>;
export type TextAnalyzeStepReturnValueType = ResultWithOauthToken<
  TextAnalyzeStepResponse,
  AppError
>;

// 解析APIで使用するデフォルト値の定数
const ANALYZE_DEFAULT_COMPANY_ID = 0;
const ANALYZE_DEFAULT_ENTRY_MANAGEMENT_ID = 0;

/**
 * AI解析APIのリクエストボディを構築するヘルパー関数
 * companyIDとuserEntryManagementIDを指定されない場合は0を設定
 */
const buildAnalyzeStepBody = <T extends Record<string, unknown>>(
  baseBody: T,
  options: {companyID?: number; userEntryManagementID?: number},
): T & {companyID: number; userEntryManagementID: number} => {
  return {
    ...baseBody,
    companyID: options.companyID ?? ANALYZE_DEFAULT_COMPANY_ID,
    userEntryManagementID:
      options.userEntryManagementID ?? ANALYZE_DEFAULT_ENTRY_MANAGEMENT_ID,
  };
};

export const textAnalyzeStep = async ({
  cookies,
  text,
  companyID,
  userEntryManagementID,
  version = DEFAULT_API_VERSION,
}: TextAnalyzeStepArgs): Promise<TextAnalyzeStepReturnValueType> => {
  const body = buildAnalyzeStepBody<TextAnalyzeStepRequestBody>(
    {text},
    {companyID, userEntryManagementID},
  );

  return analyzeStepRequest({
    cookies,
    path: `/${version}/users/me/entry-managements/steps/text-analyze`,
    body,
    schema: AnalyzeStepCandidatesResponse,
  });
};

export const useTextAnalyzeStep = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    TextAnalyzeStepReturnValueType,
    Error,
    Omit<TextAnalyzeStepArgs, 'cookies'>
  >({
    async mutationFn(args) {
      const cookies = parseCookies();
      const result = await textAnalyzeStep({...args, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }
      return result;
    },
  });
};

// POST /3.0/service/users/me/entry-managements/steps/image-analyze - エントリー管理ステップ画像AI解析作成API
type ImageAnalyzeStepArgs = {
  cookies: Partial<Record<string, string>>;
  imageBase64: string;
  imageFormat: 'jpeg' | 'jpg' | 'png' | 'gif' | 'webp';
  companyID?: number;
  userEntryManagementID?: number;
  version?: string;
};

type ImageAnalyzeStepRequestBody = {
  imageBase64: string;
  imageFormat: string;
  companyID?: number;
  userEntryManagementID?: number;
};

export type ImageAnalyzeStepResponse = z.infer<
  typeof AnalyzeStepCandidatesResponse
>;
export type ImageAnalyzeStepReturnValueType = ResultWithOauthToken<
  ImageAnalyzeStepResponse,
  AppError
>;

export const imageAnalyzeStep = async ({
  cookies,
  imageBase64,
  imageFormat,
  companyID,
  userEntryManagementID,
  version = DEFAULT_API_VERSION,
}: ImageAnalyzeStepArgs): Promise<ImageAnalyzeStepReturnValueType> => {
  const body = buildAnalyzeStepBody<ImageAnalyzeStepRequestBody>(
    {imageBase64, imageFormat},
    {companyID, userEntryManagementID},
  );

  return analyzeStepRequest({
    cookies,
    path: `/${version}/users/me/entry-managements/steps/image-analyze`,
    body,
    schema: AnalyzeStepCandidatesResponse,
  });
};

export const useImageAnalyzeStep = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    ImageAnalyzeStepReturnValueType,
    Error,
    Omit<ImageAnalyzeStepArgs, 'cookies'>
  >({
    async mutationFn(args) {
      const cookies = parseCookies();
      const result = await imageAnalyzeStep({...args, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }
      return result;
    },
  });
};

// POST /3.0/service/users/me/entry-managements/steps/report-analyze - エントリー管理ステップ体験記AI解析作成API
type ReportAnalyzeStepArgs = {
  cookies: Partial<Record<string, string>>;
  companyID: number;
  departmentID: number;
  reportType: 'intern' | 'selection';
  userEntryManagementID?: number;
  maxSteps?: number;
  version?: string;
};

type ReportAnalyzeStepRequestBody = {
  companyID: number;
  departmentID: number;
  reportType: 'intern' | 'selection';
  maxSteps?: number;
  userEntryManagementID?: number;
};

export type ReportAnalyzeStepResponse = z.infer<
  typeof AnalyzeStepCandidatesResponse
>;
export type ReportAnalyzeStepReturnValueType = ResultWithOauthToken<
  ReportAnalyzeStepResponse,
  AppError
>;

export const reportAnalyzeStep = async ({
  cookies,
  companyID,
  departmentID,
  reportType,
  userEntryManagementID,
  maxSteps = 5,
  version = DEFAULT_API_VERSION,
}: ReportAnalyzeStepArgs): Promise<ReportAnalyzeStepReturnValueType> => {
  const body = buildAnalyzeStepBody<ReportAnalyzeStepRequestBody>(
    {companyID, departmentID, reportType, maxSteps},
    {companyID, userEntryManagementID},
  );

  return analyzeStepRequest({
    cookies,
    path: `/${version}/users/me/entry-managements/steps/report-analyze`,
    body,
    schema: AnalyzeStepCandidatesResponse,
  });
};

export const useReportAnalyzeStep = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    ReportAnalyzeStepReturnValueType,
    Error,
    Omit<ReportAnalyzeStepArgs, 'cookies'>
  >({
    async mutationFn(args) {
      const cookies = parseCookies();
      const result = await reportAnalyzeStep({...args, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }
      return result;
    },
  });
};

// GET /3.0/entry_managements/reminders - リマインダー件数取得API
type GetRemindersArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export type GetRemindersResponse = z.infer<typeof RemindersResponse>;

export type GetRemindersReturnValueType = ResultWithOauthToken<
  GetRemindersResponse,
  AppError
>;

type UseGetRemindersOptions = {
  config?: UseQueryOptions<GetRemindersReturnValueType>;
};

export const getReminders = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetRemindersArgs): Promise<GetRemindersReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-managements/reminders`;

    return defaultClient.get(path, RemindersResponse);
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return result;
};

export const useGetReminders = ({config}: UseGetRemindersOptions = {}) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<GetRemindersReturnValueType>({
    ...config,
    queryKey: ['entryManagementReminders'],
    staleTime: 0, // キャッシュを無効化してリアルタイム更新を実現
    refetchOnMount: true, // マウント時に常に再取得
    async queryFn() {
      const cookies = parseCookies();
      const result = await getReminders({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

// GET /3.0/service/users/me/entry-managements/progress-missions - プログレスミッション達成状況取得
type ProgressMissionsReturnValueTypeWithOauthToken<T> = ResultWithOauthToken<
  T,
  AppError
>;

// 後方互換性のため型エイリアス
export type ProgressMissionType = MissionType;

type GetProgressMissionsArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export type GetProgressMissionsResponse = z.infer<typeof GetMissionsOutput>;

export type GetProgressMissionsReturnValueType =
  ProgressMissionsReturnValueTypeWithOauthToken<GetProgressMissionsResponse>;

type UseGetProgressMissionsOptions = {
  config?: UseQueryOptions<GetProgressMissionsReturnValueType>;
  version?: string;
};

export const getProgressMissions = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetProgressMissionsArgs): Promise<GetProgressMissionsReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const path = `/${version}/users/me/entry-managements/progress-missions`;

    return defaultClient.get<GetProgressMissionsResponse>(
      path,
      GetMissionsOutput,
    );
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return result;
};

export const useGetProgressMissions = ({
  config,
  version,
}: UseGetProgressMissionsOptions = {}) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<GetProgressMissionsReturnValueType>({
    ...config,
    queryKey: ['progressMissions', version],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getProgressMissions({
        cookies,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};
